/**
 * Хяналтын самбар — прототипийн `01 Dashboard.dc.html`-ээс хөрвүүлсэн.
 *
 * ⚠️ БҮХ ТОО БОДИТ. Хуурамч тоо, орлуулагч утга харуулахгүй.
 *
 * ЭНЭ ФАЙЛ ЗӨВХӨН ЭРЭМБЭЛНЭ. Блок бүр `components/dashboard/`-т
 * тусдаа байна — өмнө нь энэ файл 484 мөр байсныг задлав.
 *
 * МЭДЭЭЛЛИЙН ЭРЭМБЭ (дээрээс доош):
 *   1. Хэн, хэзээ            — hero
 *   2. Нөөц ямар байна       — KPI
 *   3. Хэр их зарж байна     — Орлогын график (шууд шинэчлэгддэг)
 *   4. Өртөг юунаас хамаарна — рублийн ханш
 *   5. Дэлгэрэнгүй задаргаа  — төлөв, ажилтан
 *
 * ⚠️ «Хүлээн авалт» багана диаграм ба «Байршлаар» жагсаалтыг эндээс
 *    ХАСAВ. Тэдгээр нь Тайлан хуудсанд хэвээр байгаа тул мэдээлэл
 *    алдагдаагүй — `/reports/dashboard`-ийн `intake` / `locations`
 *    талбарыг ТИЙМ УЧРААС устгаагүй.
 *
 * Анхааруулга (0₮ үнэтэй, шошгогүй, sync хийгээгүй) нь header-ийн
 * мэдэгдлийн хонхонд байна — `alertsFrom()` → `AppShell`.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  changePercent, formatDate, formatMntShort, formatQty, formatRub, reportRange,
  revenueBucketKey, ulaanbaatarDayStart,
} from '@boots/domain';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useRevenueData } from '../lib/use-revenue-data';
import {
  retailItemCount,
  type BranchReport, type Dashboard, type SalesReport,
} from '../lib/types';
import { StatCard, StatGrid } from '../components/stat-card';
import { Help } from '../components/info-tip';
import { ErrorState } from '../components/empty-state';
import { HeroBand } from '../components/dashboard/hero-band';
import { RevenueChart } from '../components/dashboard/revenue-chart';
import { FxCard } from '../components/dashboard/fx-card';
import { BranchCard } from '../components/dashboard/branch-card';
import { LeaderboardCard } from '../components/dashboard/leaderboard-card';
import { DashboardSkeleton } from '../components/dashboard/dashboard-skeleton';
import { Rise } from '../components/rise';

function pct(part: number, total: number): number {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

export function DashboardPage() {
  const { user } = useAuth();
  const { data, isPending, error, refetch } = useQuery({
    // Түлхүүр нь App.tsx-ийнхтэй ижил — мэдэгдлийн хонх, дашбоард нэг
    // хариу хуваалцана. Хугацааны сонголт нь одоо Орлогын графикт
    // байгаа тул энд сарын харагдац тогтмол.
    queryKey: ['dashboard', 'month'],
    queryFn: () => api<Envelope<Dashboard>>('/reports/dashboard?period=month'),
    placeholderData: keepPreviousData,
  });

  // «Салбарт байгаа» — дэлгүүрийн ТӨРӨЛ зөвхөн энд ирнэ. Түлхүүр нь
  // Салбар хуудсынхтай ижил тул хоёр дэлгэц нэг хариу хуваалцана.
  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => api<Envelope<BranchReport>>('/reports/branches'),
    staleTime: 60_000,
  });

  /*
    Энэ сарын борлуулалт ба ӨМНӨХ САРЫН МӨН ҮЕ.

    ⚠️ «Мөн үе» гэдэг нь чухал: сарын 25-нд бүтэн өнгөрсөн сартай
       харьцуулбал энэ сар ҮРГЭЛЖ хоцорч харагдана. `reportRange` нь
       өмнөх мужаа яг ижил урттайгаар өөрөө тавьдаг.
  */
  const month = reportRange('month', new Date());
  const monthSales = useQuery({
    queryKey: ['sales-month', month?.from.toISOString()],
    queryFn: () =>
      api<Envelope<SalesReport>>(
        `/reports/sales?from=${encodeURIComponent(month!.from.toISOString())}` +
          `&to=${encodeURIComponent(new Date(month!.to.getTime() - 1).toISOString())}`,
      ),
    enabled: !!month,
    staleTime: 60_000,
  });
  const prevSales = useQuery({
    queryKey: ['sales-prev-month', month?.prevFrom.toISOString()],
    queryFn: () =>
      api<Envelope<SalesReport>>(
        `/reports/sales?from=${encodeURIComponent(month!.prevFrom.toISOString())}` +
          `&to=${encodeURIComponent(new Date(month!.prevTo.getTime() - 1).toISOString())}`,
      ),
    enabled: !!month,
    staleTime: 60_000,
  });

  // «Өнөөдөр зарагдсан» — хилийг УБ-ийн 00:00-оор өөрсдөө зааж өгнө
  // (`from` нь бүтэн агшин, сервер өдөр гэж тайлбарладаггүй)
  const soldToday = useQuery({
    queryKey: ['sales-today'],
    queryFn: () =>
      api<Envelope<SalesReport>>(
        `/reports/sales?from=${encodeURIComponent(ulaanbaatarDayStart().toISOString())}`,
      ),
    staleTime: 60_000,
  });

  /*
    KPI-ийн sparkline — Орлогын графикийн эндпойнтоос (react-query кэш
    хуваалцана). Сар → өдөр бүрийн, өнөөдөр → цаг бүрийн. Өртөг харах
    эрхгүй бол дүнгийн оронд захиалгын ТОО.
  */
  const monthRev = useRevenueData('month').data?.data;
  const dayRev = useRevenueData('day').data?.data;

  if (isPending) return <DashboardSkeleton />;

  if (error) {
    return (
      <div className="rounded-card border border-hair bg-panel">
        <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const d = data.data;
  // `value` нь SALESPERSON-д null — өртөг харуулахгүй (эрхийн жинхэнэ
  // хамгаалалт backend дээр)
  const showValue = d.value !== null;

  const retail = retailItemCount(branches.data?.data);

  // Сарын харьцуулалт — мөнгөөр, боломжгүй бол ширхгээр
  const monthTotals = monthSales.data?.data.totals;
  const prevTotals = prevSales.data?.data.totals;
  const monthUnits = monthTotals?.units;
  const prevUnits = prevTotals?.units;
  const monthPct =
    monthTotals && prevTotals
      ? changePercent(
          showValue ? monthTotals.revenueMnt : String(monthTotals.units),
          showValue ? prevTotals.revenueMnt : String(prevTotals.units),
        )
      : null;
  const sales = soldToday.data?.data;
  const soldUnits = sales?.totals.units;

  const trend = (p: { revenueMnt: string; orderCount: number }) =>
    showValue ? Number(p.revenueMnt) : p.orderCount;
  // «day» муж нь сүүлийн 24 цаг — өнөөдрийн (УБ 00:00-оос) цагуудыг л авна
  const todayKey = revenueBucketKey('day', new Date()).slice(0, 10);
  const monthSpark = monthRev?.points.map(trend);
  const todaySpark = dayRev?.points.filter((p) => p.bucket.startsWith(todayKey)).map(trend);

  // Нийлүүлэгчийн зээл. Үлдэгдэл ₽-өөр үүсдэг тул томоор нь ₮ (өнөөдрийн
  // ханшаар), тайлбар мөрөнд жинхэнэ ₽ дүн. `credit` нь SALESPERSON-д null.
  const credit = d.credit;
  const owes = credit !== null && Number(credit.balanceRub) > 0;

  return (
    <div className="flex flex-col gap-4">
      <HeroBand />

      <StatGrid>
        <Rise i={1} shine>
        {/*
          ⚠️ Энэ карт өмнө нь «Нийт гутал» — БҮХ ЦАГИЙН тоо байв. Тэр нь
             өдөр бүр өөрчлөгддөггүй, шийдвэр гаргахад юу ч хэлдэггүй
             тоо байсан (2026-09-25). Одоо энэ сарын борлуулалт, өмнөх
             сарын МӨН ҮЕТЭЙ харьцуулсанаар. Нийт бүртгэлтэй тоо нь
             хажуугийн картын «i»-д үлдэв.
        */}
        <StatCard
          feature
          spark={monthSpark}
          label="Энэ сарын борлуулалт"
          icon="receipt"
          value={
            showValue && monthUnits !== undefined
              ? formatMntShort(monthTotals!.revenueMnt)
              : formatQty(monthUnits)
          }
          delta={{
            text:
              monthPct === null
                ? `${formatQty(monthUnits)} ширхэг`
                : `${formatQty(monthUnits)} ш · өмнөх сарын мөн үеэс ${Math.abs(monthPct)}%`,
            direction: monthPct === null ? undefined : monthPct > 0 ? 'up' : monthPct < 0 ? 'down' : 'flat',
            good: monthPct === null ? undefined : monthPct >= 0,
          }}
          pending={monthSales.isPending}
          to="/orders"
          info={
            <Help
              about={
                <>
                  {month ? (
                    <>
                      <b>{formatDate(month.from)}</b>-аас өнөөдрийг{' '}
                      өмнөх сарын <b>яг ижил урттай</b> хугацаатай харьцуулав
                      {prevUnits === undefined ? null : (
                        <> — тэр үед {formatQty(prevUnits)} ширхэг зарагдсан.</>
                      )}
                    </>
                  ) : null}{' '}
                  Буцаагдсан, хүчингүй болсон захиалга ороогүй цэвэр дүн.
                </>
              }
              howTo="Карт дээр дарж гүйлгээ бүрийг харна. Салбар, төлбөрөөр задлах бол «Тайлан»."
            />
          }
        />
        </Rise>

        <Rise i={2} shine>
        <StatCard
          feature
          label="Салбарт байгаа"
          icon="store"
          value={formatQty(retail)}
          delta={{
            text:
              retail === undefined
                ? 'салбарын тайлан ачаалж байна'
                : `${pct(retail, d.counts.total)}% нөөцөөс`,
            good: retail === undefined ? undefined : true,
          }}
          meter={pct(retail ?? 0, d.counts.total)}
          pending={branches.isPending}
          to="/branches"
          info={
            <Help
              about={
                <>
                  Салбаруудын лангуу, агуулахад байгаа гутал.{' '}
                  Системд нийт <b>{formatQty(d.counts.total)}</b> гутал бүртгэлтэй,{' '}
                  үүнээс <b>{formatQty(d.counts.sellable)}</b> нь зарагдахуйц
                  байршилд байна.
                </>
              }
              howTo="Карт дээр дарж салбар бүрийн нөөцийг харна. Размер дуусч байвал агуулахаас шилжүүлэг хийнэ."
            />
          }
        />
        </Rise>

        <Rise i={3} shine>
        <StatCard
          feature
          spark={todaySpark}
          label="Өнөөдөр зарагдсан"
          icon="receipt"
          value={formatQty(soldUnits)}
          delta={{
            text: showValue && sales ? `${formatMntShort(sales.totals.revenueMnt)} орлого` : 'ширхэг',
            good: soldUnits ? true : undefined,
          }}
          pending={soldToday.isPending}
          to="/orders"
          info={
            <Help
              about="Өнөөдөр (Улаанбаатарын цагаар 00:00-оос) зарагдсан гутал. Буцаагдсан, хүчингүй болсон захиалга орохгүй."
              howTo="Карт дээр дарж өнөөдрийн гүйлгээг харна."
            />
          }
        />
        </Rise>

        <Rise i={4} shine>
        {credit ? (
          <StatCard
            feature
            label="Нийлүүлэгчийн зээл"
            icon="coins"
            value={formatRub(credit.balanceRub)}
            delta={{
              text: owes
                ? `${formatQty(credit.suppliers)} нийлүүлэгч`
                : 'Төлөгдөөгүй өр алга',
              // Өр байгаа нь анхаарал татах мэдээ — ягаанаар
              good: owes ? false : undefined,
            }}
            meter={credit.paidPct}
            meterTone={owes ? 'alert' : 'accent'}
            to="/credit"
            info={
              <Help
                about="Оросын нийлүүлэгчдэд төлөх ёстой үлдэгдэл, рублиэр. Тууз нь нээлттэй зээлийн хэдэн хувь нь төлөгдсөнийг заана."
                howTo="Карт дээр дарж «Зээл, төлөлт» хуудсанд аялал бүрийн өр, хугацааг харна. Төлбөр бүртгэх нь админы эрх."
              />
            }
          />
        ) : (
          <StatCard
            feature
            label="Sync хийгээгүй"
            icon="sync"
            value={formatQty(d.counts.unsynced)}
            delta={{
              text: 'Оросоос ирээгүй бичлэг',
              good: d.counts.unsynced > 0 ? false : undefined,
            }}
            meter={pct(d.counts.unsynced, d.counts.total)}
            meterTone={d.counts.unsynced > 0 ? 'alert' : 'accent'}
          />
        )}
        </Rise>
      </StatGrid>

      <Rise i={5}>
        <RevenueChart />
      </Rise>

      {/*
        ⚠️ Өмнө нь блок бүр БҮТЭН ӨРГӨНӨӨР дараалан зогсож, хуудас
           уншихад урт эгнээ болж байв (2026-09-25). Одоо гол зүйл
           (өрсөлдөөн) өргөн баганад, лавлагаа (ханш, салбарын нөөц)
           нарийн баганад зэрэгцэнэ. Ханшны график ч намссан —
           энэ нь өртөгт автоматаар ордоггүй лавлагаа (дүрэм 4).
      */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        <Rise i={6} className="min-w-0">
          <LeaderboardCard myStoreId={user?.storeId ?? null} />
        </Rise>
        <div className="flex min-w-0 flex-col gap-4">
          <Rise i={7}>
            <FxCard fx={d.fx} showValue={showValue} compact />
          </Rise>
          <Rise i={8}>
            <BranchCard showValue={showValue} />
          </Rise>
        </div>
      </div>
    </div>
  );
}
